/**
 * Unified Error Taxonomy for Braise POS
 * 
 * This file defines the complete error taxonomy with stable codes, names, 
 * messages, and hints for consistent error handling across the application.
 */

// Core error response types for edge functions
export interface OkResp<T = unknown> {
  ok: true;
  data: T;
  request_id: string;
}

export interface ErrorDetails {
  name: string;        // Stable human label, e.g. "Subdomain Unavailable"
  code: string;        // Stable machine code, e.g. "DEMO.SUBDOMAIN_TAKEN"  
  message: string;     // Friendly explanation for users
  hint?: string;       // Optional next step, e.g. "Try another subdomain"
}

export interface ErrResp {
  ok: false;
  error: ErrorDetails;
  request_id: string;    // uuid v4 generated at function start
  details?: unknown;     // Optional technical data for "Show details"
}

// Union type for all edge function responses
export type ApiResponse<T = unknown> = OkResp<T> | ErrResp;

// Error taxonomy - stable codes and human-friendly messages
export const ERROR_TAXONOMY = {
  // Demo / Onboarding Errors
  DEMO: {
    SUBDOMAIN_TAKEN: {
      name: "Subdomain Unavailable",
      code: "DEMO.SUBDOMAIN_TAKEN",
      message: "That subdomain is already taken. Try another name.",
      hint: "Try a different subdomain"
    },
    SUBDOMAIN_INVALID: {
      name: "Invalid Subdomain",
      code: "DEMO.SUBDOMAIN_INVALID", 
      message: "Please use letters, numbers, and hyphens (start/end with a letter or number).",
      hint: "Use only letters, numbers, and hyphens"
    },
    RATE_LIMITED: {
      name: "Rate Limit Reached",
      code: "DEMO.RATE_LIMITED",
      message: "You've reached the demo limit. Try again later.",
      hint: "Please wait before creating another demo"
    },
    CREATE_FAILED: {
      name: "Demo Create Failed", 
      code: "DEMO.CREATE_FAILED",
      message: "We couldn't start your demo. Please try again.",
      hint: "Try again or contact support"
    },
    EXPIRED: {
      name: "Demo Expired",
      code: "DEMO.EXPIRED", 
      message: "This demo has expired. Start a new one or convert to a real account.",
      hint: "Create a new demo or sign up"
    },
    CONVERT_AUTH_REQUIRED: {
      name: "Demo Conversion Requires Login",
      code: "DEMO.CONVERT_AUTH_REQUIRED",
      message: "Please sign in to convert this demo to a real restaurant.",
      hint: "Sign in to continue"
    },
    CONVERT_FAILED: {
      name: "Demo Conversion Failed",
      code: "DEMO.CONVERT_FAILED", 
      message: "We couldn't convert this demo. Try again or contact support.",
      hint: "Try again later"
    }
  },

  // Auth / Session Errors  
  AUTH: {
    NOT_SIGNED_IN: {
      name: "Not Signed In",
      code: "AUTH.NOT_SIGNED_IN",
      message: "Please sign in to continue.",
      hint: "Sign in to access this feature"
    },
    OAUTH_FAILED: {
      name: "OAuth Provider Error", 
      code: "AUTH.OAUTH_FAILED",
      message: "Authentication failed. Please try again.",
      hint: "Try signing in again"
    },
    SESSION_EXPIRED: {
      name: "Session Expired",
      code: "AUTH.SESSION_EXPIRED",
      message: "Your session has expired. Please sign in again.",
      hint: "Sign in to continue"
    }
  },

  // POS / Orders / Kitchen Errors
  POS: {
    MENU_LOAD_FAILED: {
      name: "Menu Load Failed",
      code: "POS.MENU_LOAD_FAILED", 
      message: "Couldn't load the menu. Please try again.",
      hint: "Check your connection and retry"
    }
  },

  ORDER: {
    CREATE_FAILED: {
      name: "Order Create Failed",
      code: "ORDER.CREATE_FAILED",
      message: "We couldn't create your order. Please try again.",
      hint: "Try again or contact staff"
    },
    INVALID_TRANSITION: {
      name: "Order Transition Invalid",
      code: "ORDER.INVALID_TRANSITION", 
      message: "That order status change is not allowed.",
      hint: "Check the current order status"
    },
    NOT_FOUND: {
      name: "Order Not Found",
      code: "ORDER.NOT_FOUND",
      message: "We couldn't find that order.",
      hint: "Check the order number"
    }
  },

  // QR / Customer Errors
  QR: {
    TABLE_UNKNOWN: {
      name: "Table Unknown",
      code: "QR.TABLE_UNKNOWN",
      message: "That table link isn't valid.",
      hint: "Ask staff for a new QR code."
    },
    RESTAURANT_UNKNOWN: {
      name: "Restaurant Unknown",
      code: "QR.RESTAURANT_UNKNOWN",
      message: "Restaurant not found or inactive.",
      hint: "Check if the QR code is from the correct restaurant."
    },
    PUBLIC_TOKEN_INVALID: {
      name: "Order Token Invalid",
      code: "QR.PUBLIC_TOKEN_INVALID", 
      message: "This order link is invalid or expired.",
      hint: "Contact the restaurant for help"
    },
    TOKEN_MISSING: {
      name: "QR Token Missing",
      code: "QR.TOKEN_MISSING",
      message: "This QR link is missing a signature.",
      hint: "Scan a fresh QR code provided by staff."
    },
    TOKEN_SIGNATURE_INVALID: {
      name: "QR Token Invalid",
      code: "QR.TOKEN_SIGNATURE_INVALID",
      message: "This QR link signature is invalid.",
      hint: "Request a new QR placard from staff."
    },
    TOKEN_INVALID: {
      name: "QR Token Invalid",
      code: "QR.TOKEN_INVALID",
      message: "This QR link could not be read.",
      hint: "Refresh the page or scan a new QR code."
    },
    TOKEN_EXPIRED: {
      name: "QR Token Expired",
      code: "QR.TOKEN_EXPIRED",
      message: "This QR link has expired.",
      hint: "Ask the team for a fresh QR code."
    },
    CONTEXT_MISMATCH: {
      name: "QR Context Mismatch",
      code: "QR.CONTEXT_MISMATCH",
      message: "This QR link doesn't match the table anymore.",
      hint: "Refresh with an updated QR from the restaurant."
    }
  },

  // Domain Errors
  DOMAIN: {
    UNVERIFIED: {
      name: "Domain Unverified",
      code: "DOMAIN.UNVERIFIED",
      message: "Custom domain not yet verified.",
      hint: "You can still use the preview URL while domain verification completes."
    }
  },

  // Payment Errors (provider-agnostic)
  PAY: {
    INIT_FAILED: {
      name: "Payment Init Failed",
      code: "PAY.INIT_FAILED",
      message: "We couldn't start payment processing. Please try again.",
      hint: "Try again or use a different payment method"
    },
    DECLINED: {
      name: "Payment Declined", 
      code: "PAY.DECLINED",
      message: "Your payment was declined. Please try a different card.",
      hint: "Try a different payment method"
    },
    REFUND_NOT_ALLOWED: {
      name: "Refund Not Allowed",
      code: "PAY.REFUND_NOT_ALLOWED",
      message: "This payment cannot be refunded at this time.",
      hint: "Contact support for assistance"
    }
  },

  // Network / System Errors
  NET: {
    OFFLINE: {
      name: "Network Error",
      code: "NET.OFFLINE", 
      message: "Network connection issue.",
      hint: "Check your internet connection and try again."
    }
  },

  // Edge Function Errors
  EDGE: {
    "5XX": {
      name: "Server Error",
      code: "EDGE.5XX",
      message: "Server error occurred.",
      hint: "Please try again in a moment."
    }
  },

  APP: {
    UNEXPECTED: {
      name: "Unexpected Error",
      code: "APP.UNEXPECTED",
      message: "Something went wrong. Please try again.",
      hint: "Try again or contact support"
    }
  }
} as const;

// Helper function to get error details by code
export function getErrorByCode(code: string): ErrorDetails | null {
  const parts = code.split('.');
  if (parts.length !== 2) return null;
  
  const [category, errorType] = parts;
  const categoryErrors = ERROR_TAXONOMY[category as keyof typeof ERROR_TAXONOMY];
  
  if (!categoryErrors) return null;
  
  const error = categoryErrors[errorType as keyof typeof categoryErrors];
  return error || null;
}

// Helper function to create a fallback error 
export function createFallbackError(message?: string): ErrorDetails {
  return {
    name: "Unexpected Error",
    code: "APP.UNEXPECTED",
    message: message || "Something went wrong. Please try again.",
    hint: "Try again or contact support"
  };
}

// Generate a request ID for edge functions
export function generateRequestId(): string {
  return crypto.randomUUID();
}

// Helper to map common exceptions to taxonomy
export function mapErrorToTaxonomy(error: unknown, context?: string): ErrorDetails {
  const errorStr = String(error);
  
  // Demo-specific mappings
  if (context === 'demo') {
    if (errorStr.includes('already taken') || errorStr.includes('subdomain_unique')) {
      return ERROR_TAXONOMY.DEMO.SUBDOMAIN_TAKEN;
    }
    if (errorStr.includes('invalid subdomain') || errorStr.includes('validation')) {
      return ERROR_TAXONOMY.DEMO.SUBDOMAIN_INVALID;
    }
    if (errorStr.includes('rate limit') || errorStr.includes('too many')) {
      return ERROR_TAXONOMY.DEMO.RATE_LIMITED;
    }
    if (errorStr.includes('expired')) {
      return ERROR_TAXONOMY.DEMO.EXPIRED;
    }
    if (errorStr.includes('auth') || errorStr.includes('unauthorized')) {
      return ERROR_TAXONOMY.DEMO.CONVERT_AUTH_REQUIRED;
    }
    return ERROR_TAXONOMY.DEMO.CREATE_FAILED;
  }

  // Order-specific mappings
  if (context === 'order') {
    if (errorStr.includes('not found')) {
      return ERROR_TAXONOMY.ORDER.NOT_FOUND;
    }
    if (errorStr.includes('transition') || errorStr.includes('invalid status')) {
      return ERROR_TAXONOMY.ORDER.INVALID_TRANSITION;
    }
    return ERROR_TAXONOMY.ORDER.CREATE_FAILED;
  }

  // Auth-specific mappings
  if (context === 'auth') {
    if (errorStr.includes('session') || errorStr.includes('expired')) {
      return ERROR_TAXONOMY.AUTH.SESSION_EXPIRED;
    }
    if (errorStr.includes('oauth') || errorStr.includes('provider')) {
      return ERROR_TAXONOMY.AUTH.OAUTH_FAILED;
    }
    return ERROR_TAXONOMY.AUTH.NOT_SIGNED_IN;
  }

  // Network-specific mappings
  if (errorStr.includes('fetch') || errorStr.includes('network') || errorStr.includes('offline')) {
    return ERROR_TAXONOMY.NET.OFFLINE;
  }

  // Default fallback
  return createFallbackError(errorStr);
}
